import { FC, useEffect, useRef, useState } from 'react';
import { IconButton, Input, Stack, TextTitle } from '@pypestream/design-system';

import { useProStudioViewerRole, useSolutionNameValidation } from '../hooks';
import { getEditableSolutionName } from '../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import './solution-name-editor.scss';
import { solutionNameToSave } from './solution-name';

/**
 * Project Bobcat — the solution's name on the Design stage, with a pencil to
 * rename it. The five-stage nav redirects the Versions page, the only other
 * place a solution is renamed, so this is where it happens there. Same
 * validation (1–100 characters, unique in the project) and the same save
 * (`updateSolutionName`) as the Versions page. Viewers see the name only.
 */
export const SolutionNameEditor: FC = () => {
  const isViewer = useProStudioViewerRole();
  const { displayName, solutionId } = useProStudioCtxSelector((ctx) => ({
    displayName: ctx.selectedBot?.displayName,
    solutionId: ctx.selectedBot?.uniqueId,
  }));
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(getEditableSolutionName(displayName));
  const { hasLengthError, isValidName, validateSolutionName } =
    useSolutionNameValidation(name);

  // Into the field as it opens, once the web component has rendered it.
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!editing) return;
    const field = wrapRef.current?.querySelector('ps-input') as
      | (HTMLElement & { updateComplete?: Promise<unknown> })
      | null;
    void Promise.resolve(field?.updateComplete).then(() => field?.focus());
  }, [editing]);

  // Another solution opened, or the save came back: start from its name.
  useEffect(() => {
    setName(getEditableSolutionName(displayName));
    setEditing(false);
  }, [displayName, solutionId]);

  const cancel = () => {
    setName(getEditableSolutionName(displayName));
    setEditing(false);
  };
  const save = () => {
    const next = solutionNameToSave(name, displayName);
    setEditing(false);
    if (!next || !solutionId) return;
    sendProStudioEvent({ type: 'updateSolutionName', name: next, solutionId });
  };
  const unchanged = solutionNameToSave(name, displayName) === null;

  if (!editing) {
    return (
      <Stack gutter="xsmall" alignItems="center">
        <TextTitle size="xsmall" test-id="bobcat-solution-name">
          {displayName || 'Untitled solution'}
        </TextTitle>
        {!isViewer && solutionId && (
          <IconButton
            name="pencil"
            size="small"
            aria-label="Rename solution"
            test-id="bobcat-solution-name-edit"
            onClick={() => setEditing(true)}
          />
        )}
      </Stack>
    );
  }

  return (
    <div className="c-solution-name-editor" ref={wrapRef}>
      <Input
        value={name}
        name="solution-name"
        size="small"
        test-id="bobcat-solution-name-input"
        hasError={hasLengthError}
        helpText={
          hasLengthError
            ? 'Use 1–100 characters.'
            : !unchanged && !isValidName
              ? 'Another solution in this project has that name.'
              : undefined
        }
        // @ts-expect-error - onInput event type has no value property
        onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
          setName(validateSolutionName(e.target.value))
        }
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' && !unchanged && isValidName) save();
          if (e.key === 'Escape') cancel();
        }}
      />
      <IconButton
        name="check"
        size="small"
        aria-label="Save name"
        test-id="bobcat-solution-name-save"
        disabled={unchanged || !isValidName}
        onClick={save}
      />
      <IconButton
        name="close"
        size="small"
        aria-label="Cancel"
        test-id="bobcat-solution-name-cancel"
        onClick={cancel}
      />
    </div>
  );
};
